import { DailyRecurrenceDetails, HourlyRecurrenceDetails, MonthlyRecurrenceDetails, Recurrence, RecurrenceConverters, RecurrenceMode, RecurrenceType, StaticInitializers } from '@tableau/self-service-schedules';
import { LocalizationHelpers } from '@tableau/self-service-schedules/lib/src/components/CreateOrEditRecurrenceModelMapper';
import { appStore } from '../../app/AppStore';
import { SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { FeatureFlags } from '../FeatureFlags';
import { ScheduleFrequencyDescriptionFilterFromSessionInfo, describeLegacyAdminSchedule } from '../filters/ScheduleFrequencyDescriptionFilter';
import { ISchedule, ScheduleTypes } from '../ServerApi/types/ISchedule';

export interface IScheduleInfo extends ISchedule {
  contentType: 'schedule';
}

export namespace IScheduleInfo {
  export function toSSSRecurrence(schedule: ISchedule): Recurrence {
    let recurrence = {
      recurrenceDetails: schedule.scheduleDetails as any,
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: schedule.scheduleType as RecurrenceType,
    };
    type invalid = any;
    switch (recurrence.recurrenceType) {
      case RecurrenceType.Hourly:
        // SSS expects both Hourly and Daily schedules to have recurrenceDaysOfWeek to be set. In the Admin Schedules these schedule
        // types did not have those values set, and that was interpretted to mean "every day of the week"
        if (!('recurrenceDaysOfWeek' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceDaysOfWeek = StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK;
        }
        break;
      case RecurrenceType.Daily:
        // SSS expects both Hourly and Daily schedules to have recurrenceDaysOfWeek to be set. In the Admin Schedules these schedule
        // types did not have those values set, and that was interpretted to mean "every day of the week"
        if (!('recurrenceDaysOfWeek' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceDaysOfWeek = StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK;
        }
        // SSS expects end time to equal start time if the schedule runs "all day"
        if (!('endTime' in recurrence.recurrenceDetails)) { // ends when it starts...
          (recurrence.recurrenceDetails as invalid).endTime = (recurrence.recurrenceDetails as invalid).startTime;
        }
        // SSS expects recurrence minutes to be the number of minutes in a day if the schedule runs on a 24 hour recurrence (daily)
        if (!('recurrenceMinutes' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceMinutes = StaticInitializers.MINUTES_IN_DAY;
        }
        // SSS does not allow recurrence minutes to be less than 2 hours for daily. If it is it's a legacy schedule
        if (recurrence.recurrenceDetails.recurrenceMinutes < 60 * 2) {
          (recurrence.recurrenceDetails as invalid).recurrenceMinutes = StaticInitializers.MINUTES_IN_DAY;
        }
        break;
    }
    return recurrence;
  }

  /**
   * Warning: May throw unexpected exceptions if conversion fails
   */
  export function fromSSSchedule(schedule: ISchedule): ISchedule {
    switch (schedule.scheduleType) { // Hourly, Daily, Weekly, Monthly
      case 'Hourly':
      case 'Daily':
        if ((schedule.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek && (schedule.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek.length === 7) {
          delete (schedule.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek;
        }
        break;
      case 'Monthly':
        let daysOfMonthMask = (schedule.scheduleDetails as MonthlyRecurrenceDetails).recurrenceDayOfMonth;
        let daysOfMonth = RecurrenceConverters.toDaysOfMonthArr(daysOfMonthMask);
        if (daysOfMonth.length !== 1) {
          throw 'Failed to convert schedule: multiple days of month present';
        }
        (schedule.scheduleDetails as MonthlyRecurrenceDetails).recurrenceDayOfMonth = String(daysOfMonth[0]);
        break;
    }
    return schedule;
  }

  export function getScheduleDescription(schedule: ISchedule): string {
    if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
      // Always try to use the old description logic as it is (as of writing this) nicer
      try {
        const legacySchedule = fromSSSchedule(schedule);
        return describeLegacyAdminSchedule(legacySchedule);
      } catch (exception) {
        const recurrence = toSSSRecurrence(schedule);
        return LocalizationHelpers.getRecurrenceStringLocalized(recurrence);
      }
    } else {
      // No need for any conversion if it's already a legacy schedule
      return describeLegacyAdminSchedule(schedule);
    }
  }
}
